import { faCheckSquare } from "@fortawesome/free-regular-svg-icons/faCheckSquare";
import { faPrint } from "@fortawesome/free-solid-svg-icons/faPrint";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import { faXmarkCircle } from "@fortawesome/free-solid-svg-icons/faXmarkCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Legenda from "app/components/itens-orcamento/legenda/Legenda";
import { DadosCancelarOrcamento, ItemPedido, NecessitaAutorizacaoExclusaoFinanceiro, PedidoCancelamento } from "app/types/pesquisa-orcamento/CancelarOrcamento";
import clsx from "clsx";
import { FormikErrors, useFormik } from "formik";
import React, { useState, useMemo, useCallback, useRef, memo } from "react";
import globalMU from "globalMU";
import { InputData } from "app/components/inputs/InputData";

const estilosBotoes = require("../../../estilos/botoes.module.scss").default;
const estilosTabelas = require('../../../estilos/tabelas.module.scss').default;
const estilosInputs = require('../../../estilos/inputs.module.scss').default;

export type CancelarOrcamentoProps = {
    dadosCancelarOrcamento: Partial<DadosCancelarOrcamento>;
    itemACancelar: Partial<PedidoCancelamento>;
    itemSelecionados: ItemPedido[];
    processandoCancelamento: boolean;
    inicializando: boolean;
    mostrarAutorizacao: Partial<NecessitaAutorizacaoExclusaoFinanceiro>;
    jaCancelado: boolean;
    corSelecionada: string;
    textoSelecionado: string;
    permitidoImprimirComprovanteCredito: boolean;
    permitidoImprimirReciboBaixaCancelamento: boolean; // 476757
    mostrarDataCancelamento: boolean;
    onSelecionaTodosItens: () => void;
    onPesquisarCliente: (pedido: Partial<PedidoCancelamento>) => void;
    onLimparCliente: () => void;
    onSelecionarItem: (itemPedido: ItemPedido) => void;
    onErrorValidacao: (erros: FormikErrors<PedidoCancelamento>) => void;
    onVerificarAutorizacaoCancelarOrcamento: (pedido: Partial<PedidoCancelamento>) => void;
    openModalImpressao: (tipo: 'ComprovanteCreditoCancelamento' | 'ReciboBaixaCancelamento') => void;
    onLiberarPendencia: (numeroPedido: number, senha: string) => void;
};

export const CancelarOrcamento = memo((props: CancelarOrcamentoProps) => {
    const [formularioValidado, setFormularioValidado] = useState<boolean>(false);
    const [senha, setSenha] = useState<string>('');
    const existeItemParaCancelar = props.itemSelecionados?.some(i => i.MensagemErro === ""); // 488761
    const orcamentoUnificado = props.itemACancelar?.OrcamentoUnificado;

    const formik = useFormik({
        initialValues: { ...props.itemACancelar } as PedidoCancelamento,
        onSubmit: () => {}, // Função de submit vazia, pois o formulário é controlado manualmente
        enableReinitialize: true,
        validate: (values: PedidoCancelamento) => {
            const mensagemObrigatorio = (campo: string) => `O campo "${campo}" é obrigatório.`;
            const mensagemDataVenda = () => `Não é permitido data do cancelamento inferior a data atual para Cancelamento Parcial.`;
    
            const erros: FormikErrors<PedidoCancelamento> = {};
    
            if (!values.CodigoMotivo) {
                erros.CodigoMotivo = mensagemObrigatorio("Motivo");
            }
    
            if (!values.DataVenda) {
                erros.DataVenda = mensagemObrigatorio("Data do cancelamento");
            } else if (
                props.mostrarDataCancelamento &&
                globalMU.formatDate(values.DataVenda) !== globalMU.converterObjetoDateParaDataFormatoPT(new Date()) &&
                values?.Itens?.length !== props.itemSelecionados?.length
            ) {
                erros.DataVenda = mensagemDataVenda();
            }
    
            return erros;
        },
    });

    // Ref para capturar o valor mais recente de formik.values no momento do clique,
    // sem precisar colocar formik.values nas deps dos callbacks (evita recriar callbacks a cada digitação)
    const formikValuesRef = useRef(formik.values);
    formikValuesRef.current = formik.values;

    const handlePesquisarCliente = useCallback(() => {
        props.onPesquisarCliente(formikValuesRef.current);
    }, [props.onPesquisarCliente]);

    const handleLimparCliente = useCallback(() => {
        formik.setFieldValue("NomeCliente", "");
        props.onLimparCliente();
    }, [props.onLimparCliente]); // formik.setFieldValue é estável (garantido pelo formik)

    const handleSalvar = useCallback(async () => {
        const erros = await formik.validateForm(formikValuesRef.current);
        Object.keys(erros).length === 0
            ? props.onVerificarAutorizacaoCancelarOrcamento(formikValuesRef.current)
            : props.onErrorValidacao(erros);
        setFormularioValidado(true);
    }, [formik.validateForm, props.onVerificarAutorizacaoCancelarOrcamento, props.onErrorValidacao]);

    const handleSelecionarTodos = useCallback(() => {
        props.onSelecionaTodosItens();
    }, [props.onSelecionaTodosItens]);

    const onChangeCodigoMotivo = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        formik.setFieldValue("CodigoMotivo", e.target.value);
    }, [formik.setFieldValue]);

    const onChangeSituacaoFinanceira = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        formik.setFieldValue("SituacaoFinanceira", e.target.value);
    }, [formik.setFieldValue]);

    const onChangeSituacaoFiscal = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        formik.setFieldValue("SituacaoFiscal", e.target.value);
    }, [formik.setFieldValue]);

    const onChangeDataVenda = useCallback((data: string) => {
        formik.setFieldValue("DataVenda", data);
    }, [formik.setFieldValue]);

    const handleSenhaChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSenha(e.target.value);
    }, []);

    const handleLiberarPendencia = useCallback(() => {
        props.onLiberarPendencia(formikValuesRef.current.Numero, senha);
    }, [props.onLiberarPendencia, senha]);

    const opcoesMotivos = useMemo(() => (
        props.dadosCancelarOrcamento?.MotivosPerdaCotacao?.map((value) => (
            <option key={value.Codigo} value={value.Codigo}
                id={`CancelarOrcamento_ComboMotivo_Opcao_${value.Codigo}`}>
                {value.DescricaoCombo}
            </option>
        ))
    ), [props.dadosCancelarOrcamento?.MotivosPerdaCotacao]);

    const renderDadosPedido = useMemo(() => (
        <div className="_my-4" id="cancelamento_dadosPedido">
            <div className="_flex _items-center">
                <label className="_w-[190px] _bg-[#e4e6e8] _text-right _font-normal _mb-0 _p-2">Nº Orçamento</label>
                <p className="_mb-0 _p-2">{props.dadosCancelarOrcamento?.Pedido?.Numero}</p>
            </div>
            <div className="_flex _items-center">
                <label className="_w-[190px] _bg-[#e4e6e8] _text-right _font-normal _mb-0 _p-2">Situação</label>
                <p className="_mb-0 _p-2">{props.dadosCancelarOrcamento?.Pedido?.DescricaoSituacao}</p>
            </div>
            <div className="_flex _items-center">
                <label className="_w-[190px] _bg-[#e4e6e8] _text-right _font-normal _mb-0 _p-2">Filial</label>
                <p className="_mb-0 _p-2">{props.dadosCancelarOrcamento?.Pedido?.Filial}</p>
            </div>
            {props.dadosCancelarOrcamento?.NecessitaSelecionarCliente ? (
                <div className="_flex _items-center" id="cancelamento_dadosPedido_Cliente">
                    <label className="_w-[190px] _bg-[#e4e6e8] _text-right _font-normal _mb-0 _p-2">Cliente</label>
                    <p className="_mb-0 _text-[#ff0000]">{props.itemACancelar?.NomeCliente}</p>
                    <div className="flex _gap-x-3">
                        <FontAwesomeIcon icon={faSearch} color="#3c8ac9" className="_text-2xl _mx-2" cursor="pointer"
                            onClick={handlePesquisarCliente} id="CancelarOrcamento_BotaoPesquisarCliente" key="CancelarOrcamento_BotaoPesquisarCliente" />
                        <FontAwesomeIcon icon={faXmarkCircle} color="#f25713" className="_text-2xl" cursor="pointer"
                            onClick={handleLimparCliente} id="CancelarOrcamento_BotaoLimparCliente" key="CancelarOrcamento_BotaoLimparCliente" />
                    </div>
                </div>
            ) : (
                <div className="_flex _items-center">
                    <label className="_w-[190px] _bg-[#e4e6e8] _text-right _font-normal _mb-0 _p-2">Cliente</label>
                    <p className="_mb-0 _p-2">{props.itemACancelar?.NomeCliente}</p>
                </div>
            )}
            <div className="_flex _items-center">
                <label className="_w-[190px] _bg-[#e4e6e8] _text-right _font-normal _mb-0 _p-2">Vendedor</label>
                <p className="_mb-0 _p-2">{props.dadosCancelarOrcamento?.Pedido?.NomeVendedor}</p>
            </div>
        </div>
    ), [
        props.dadosCancelarOrcamento?.Pedido?.Numero,
        props.dadosCancelarOrcamento?.Pedido?.DescricaoSituacao,
        props.dadosCancelarOrcamento?.Pedido?.Filial,
        props.dadosCancelarOrcamento?.Pedido?.NomeVendedor,
        props.dadosCancelarOrcamento?.NecessitaSelecionarCliente,
        props.itemACancelar?.NomeCliente,
        handlePesquisarCliente,
        handleLimparCliente,
    ]);

    const renderTabelaItens = useMemo(() => (
        <div>
            <table className={`_table _w-full _border-collapse ${estilosTabelas.tabela}`}>
                <thead>
                    <tr>
                        <th colSpan={3}>Itens do Orçamento</th>
                        <th colSpan={2}>Retirada</th>
                        <th colSpan={2}>Transferência</th>
                        <th colSpan={2}>Faturamento</th>
                        <th rowSpan={2}>Observação</th>
                    </tr>
                    <tr>
                        <th>Código</th>
                        <th>Produto</th>
                        <th>Quantidade</th>
                        <th>Filial</th>
                        <th>Nota Fiscal/Série</th>
                        <th>Filial</th>
                        <th>Nota Fiscal/Série</th>
                        <th>Filial</th>
                        <th>Nota Fiscal/Série</th>
                    </tr>
                </thead>
                <tbody>
                    {formik.values?.Itens?.map((item, index) => (
                        <tr key={`${item.Codigo}_${index}`}
                             id={`CancelarOrcamento_linhaItem_${item.Codigo}_${index}`}
                            className="_table-row _cursor-pointer"
                            onClick={() => !orcamentoUnificado && props.onSelecionarItem(item)}
                            style={props.itemSelecionados.some(p => p.Codigo === item.Codigo) ? {
                                backgroundColor: props.corSelecionada,
                                color: props.textoSelecionado,
                            } : {}}
                        >
                            <td style={{ boxShadow: 'inset 4px 0px' + item.CorLegenda }}>{item.CodigoProdutoExibicao}</td>
                            <td>{item.DescricaoProduto}</td>
                            <td>{item.Quantidade}</td>
                            <td>{item.FilialRetirada}</td>
                            <td>{item.NfRetirada}</td>
                            <td>{item.FilialTransferencia}</td>
                            <td>{item.NfTransferencia}</td>
                            <td>{item.FilialFaturamentoMostrar}</td>
                            <td>{item.NfFaturamento}</td>
                            <td className="_text-center">
                                <input className={clsx("_w-full", estilosInputs.inputPadrao)} type="text"
                                    key={`${item.Codigo}`}
                                    id={`CancelarOrcamento_CampoObservacaoItem_${item.Codigo}_${index}`}
                                    onClick={(e) => e.stopPropagation()}
                                    value={item.ObservacaoItem ?? ''}
                                    name={`Itens[${index}].ObservacaoItem`}
                                    disabled={orcamentoUnificado || props.processandoCancelamento || !props.itemSelecionados.some(i => i.Codigo == item.Codigo)}
                                    onChange={(e) => formik.setFieldValue(`Itens[${index}].ObservacaoItem`, e.target.value)}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    ), [formik.values.Itens, props.itemSelecionados, props.corSelecionada, props.textoSelecionado, props.processandoCancelamento, orcamentoUnificado, props.onSelecionarItem]);

    const renderBotaoSalvar = useMemo(() => (
        <div className="_my-2">
            <button className={`_flex _my-2 no-global-button ${estilosBotoes.botaoPrimario}`}
                id="CancelarOrcamento_BotaoSalvar"
                key="CancelarOrcamento_BotaoSalvar"
                onClick={handleSalvar}
                disabled={props.mostrarAutorizacao?.NecessitaAutorizacao || props.processandoCancelamento}
            >
                {props.processandoCancelamento ? "Aguarde, cancelamento orçamento..." : "Salvar"}
            </button>
        </div>
    ), [handleSalvar, props.mostrarAutorizacao?.NecessitaAutorizacao, props.processandoCancelamento]);

    const renderBotaoSelecionarTodos = useMemo(() => (
        !props.jaCancelado && !orcamentoUnificado && (
            <div className="_my-4">
                <button className={`_flex _my-2 no-global-button ${estilosBotoes.botaoIcone}`}
                    id="CancelarOrcamento_BotaoSelecionarTodos"
                    title="Inverte a seleção"
                    onClick={handleSelecionarTodos}
                >
                    <FontAwesomeIcon icon={faCheckSquare} color="#3c8ac9" className="_text-2xl"
                    id="CancelarOrcamento_IconeSelecionarTodos"
                    key="CancelarOrcamento_IconeSelecionarTodos"
                    />
                    Selecionar todos
                </button>
            </div>
        )
    ), [props.jaCancelado, orcamentoUnificado, handleSelecionarTodos]);

    const renderDataCancelamento = useMemo(() => (
        props.mostrarDataCancelamento && (
            <div className="_flex _items-center _mt-2">
                <label className={clsx("_w-1/5 _text-right _font-normal _mb-0 _p-2", estilosInputs.labelObrigatorio)}>Data do cancelamento</label>
                <InputData
                    className={clsx("_w-[200px] _p-2", formularioValidado && formik.errors.DataVenda !== undefined && estilosInputs.inputComErro)}
                    data={globalMU.converterDataHoraFormatoENComTParaDataFormatoPT(formik.values.DataVenda)}
                    onChange={onChangeDataVenda}
                    disabled={formik.values?.Itens?.length !== props.itemSelecionados?.length || props.processandoCancelamento}
                    selecionaValorOnFocus
                    id="CancelarOrcamento_CampoDataVenda"
                />
            </div>
        )
    ), [
        props.mostrarDataCancelamento,
        formularioValidado,
        formik.errors.DataVenda,
        formik.values.DataVenda,
        formik.values?.Itens?.length,
        props.itemSelecionados?.length,
        props.processandoCancelamento,
        onChangeDataVenda,
    ]);

    const renderFormularioMotivoSituacao = useMemo(() => (
        <div className="_my-6">
            <table className={`_table _w-full _border-collapse ${estilosTabelas.tabela}`}>
                <tbody>
                    {!props.jaCancelado && (
                        <tr>
                            <th className="_w-1/5 !_text-right">Observação</th>
                            <td className="_w-4/5 _p-2">
                                <textarea maxLength={254} rows={2} className="_w-full _mt-2"
                                    name="Observacao"
                                    id="CancelarOrcamento_CampoObservacaoGeral"
                                    disabled={props.processandoCancelamento || props.jaCancelado || orcamentoUnificado }
                                    value={formik.values.Observacao ?? ''}
                                    onChange={formik.handleChange}>
                                </textarea>
                            </td>
                        </tr>
                    )}
                    <tr>
                        <th className={clsx("_w-1/5 !_text-right", estilosInputs.labelObrigatorio)}>Motivo</th>
                        <td className="_w-4/5 _p-2">
                            <select name="Motivo"
                                id="CancelarOrcamento_ComboMotivo"
                                value={formik.values.CodigoMotivo ?? ''}
                                disabled={props.processandoCancelamento || props.jaCancelado}
                                className={clsx("_w-[230px]", estilosInputs.inputPadrao, formularioValidado && formik.errors.CodigoMotivo !== undefined && estilosInputs.inputComErro)}
                                onChange={onChangeCodigoMotivo}>
                                <option value="">Selecione</option>
                                {opcoesMotivos}
                            </select>
                        </td>
                    </tr>
                    <tr>
                        <th className="_w-1/5 !_text-right">Situação Financeira</th>
                        <td className="_w-4/5 _p-2">
                            <select name="SituacaoFinanceira"
                                id="CancelarOrcamento_ComboSituacaoFinanceira"
                                key="CancelarOrcamento_ComboSituacaoFinanceira"
                                value={formik.values.SituacaoFinanceira ?? ''}
                                disabled={props.processandoCancelamento || props.jaCancelado}
                                className={clsx("_w-[105px] _mr-2", estilosInputs.inputPadrao)}
                                onChange={onChangeSituacaoFinanceira}>
                                <option value="" id="CancelarOrcamento_ComboSituacaoFinanceira_Vazio"></option>
                                <option value="Solucionado" id="CancelarOrcamento_ComboSituacaoFinanceira_Solucionado">Solucionado</option>
                                <option value="Pendente" id="CancelarOrcamento_ComboSituacaoFinanceira_Pendente">Pendente</option>
                            </select>
                            <input type="text" name="ObservacaoSituacaoFinanceira"
                                id="CancelarOrcamento_CampoObservacaoSituacaoFinanceira"
                                key="CancelarOrcamento_CampoObservacaoSituacaoFinanceira"
                                value={formik.values.ObservacaoSituacaoFinanceira ?? ''}
                                disabled={props.processandoCancelamento || props.jaCancelado}
                                title={formik.values.ObservacaoSituacaoFinanceira}
                                className={clsx("_ml-2", estilosInputs.inputPadrao)}
                                onChange={formik.handleChange} />
                        </td>
                    </tr>
                    <tr>
                        <th className="_w-1/5 !_text-right">Situação Fiscal</th>
                        <td className="_w-4/5 _p-2">
                            <select name="SituacaoFiscal"
                                id="CancelarOrcamento_ComboSituacaoFiscal"
                                value={formik.values.SituacaoFiscal ?? ''}
                                disabled={props.processandoCancelamento || props.jaCancelado}
                                onChange={onChangeSituacaoFiscal}
                                className={clsx("_w-[105px] _mr-2", estilosInputs.inputPadrao)}
                            >
                                <option value="" id="CancelarOrcamento_ComboSituacaoFiscal_Vazio"></option>
                                <option value="Solucionado" id="CancelarOrcamento_ComboSituacaoFiscal_Solucionado">Solucionado</option>
                                <option value="Pendente" id="CancelarOrcamento_ComboSituacaoFiscal_Pendente">Pendente</option>
                            </select>
                            <input type="text" value={formik.values.ObservacaoSituacaoFiscal ?? ''}
                                id="CancelarOrcamento_CampoObservacaoSituacaoFiscal"
                                key="CancelarOrcamento_CampoObservacaoSituacaoFiscal"
                                name="ObservacaoSituacaoFiscal"
                                disabled={props.processandoCancelamento || props.jaCancelado}
                                title={formik.values.ObservacaoSituacaoFiscal}
                                className={clsx("_ml-2", estilosInputs.inputPadrao)}
                                onChange={formik.handleChange} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    ), [
        props.jaCancelado,
        props.processandoCancelamento,
        orcamentoUnificado,
        formik.values.Observacao,
        formik.values.CodigoMotivo,
        formik.values.SituacaoFinanceira,
        formik.values.SituacaoFiscal,
        formik.values.ObservacaoSituacaoFinanceira,
        formik.values.ObservacaoSituacaoFiscal,
        formik.handleChange,
        formularioValidado,
        formik.errors.CodigoMotivo,
        opcoesMotivos,
        onChangeCodigoMotivo,
        onChangeSituacaoFinanceira,
        onChangeSituacaoFiscal,
    ]);

    const renderAutorizacao = useMemo(() => (
        props.mostrarAutorizacao?.NecessitaAutorizacao && (
            <div className="_my-4 _bg-[#e4e6e8] _p-4">
                <div className="_flex _items-center _mt-2 _w-full">
                    <p>{props.mostrarAutorizacao?.Mensagem}</p>
                    <label className={clsx("_w-1/5 _text-right _font-normal _mb-0 _p-2", estilosInputs.labelObrigatorio)}>Senha</label>
                    <input type="password" value={(senha) ?? ""} className={clsx("_ml-2", estilosInputs.inputPadrao)}
                        onChange={handleSenhaChange}
                        autoComplete="off"
                        list="autocompleteOff" // para naos mostrar dados digitados anteriormente
                        aria-autocomplete="none"
                        id="CancelarOrcamento_CampoSenhaAutorizacao"
                        key="CancelarOrcamento_CampoSenhaAutorizacao"
                    />
                    <button className={`_mx-4 _flex _my-2 no-global-button ${estilosBotoes.botaoPrimario}`}
                        onClick={handleLiberarPendencia}
                        id="CancelarOrcamento_LiberarPendencia"
                        key="CancelarOrcamento_LiberarPendencia"
                    >
                        Liberar Pendência
                    </button>
                </div>
            </div>
        )
    ), [props.mostrarAutorizacao?.NecessitaAutorizacao, props.mostrarAutorizacao?.Mensagem, senha, handleSenhaChange, handleLiberarPendencia]);

    return (
        <>
            {props.inicializando && <span>Aguarde... Buscando dados do orçamento!</span>}
            {!props.permitidoImprimirComprovanteCredito  && !props.permitidoImprimirReciboBaixaCancelamento // 476757
                        && props.dadosCancelarOrcamento?.Pedido?.Numero > 0 && (
                <>
                    {renderDadosPedido}
                    {renderBotaoSelecionarTodos}
                    {renderTabelaItens}
                    {renderDataCancelamento}
                    {renderFormularioMotivoSituacao}
                    {renderAutorizacao}
                    {existeItemParaCancelar && !props.jaCancelado && props.itemSelecionados.length > 0 && renderBotaoSalvar}
                    <div className="_my-4" id="CancelarOrcamento_Legenda">
                        <Legenda itens={[{ nome: "Já Cancelado / Não Pode Ser Cancelado", cor: "#FF0000" }]} />
                    </div>
                    <section style={{ marginBottom: "100px" }} />
                </>
            )}
            {props.permitidoImprimirComprovanteCredito && (
                <button className="_my-6 no-global-button"
                    id="CancelarOrcamento_BotaoImprimirComprovante"
                    key="CancelarOrcamento_BotaoImprimirComprovante"
                    onClick={() => props.openModalImpressao('ComprovanteCreditoCancelamento')}>
                    <FontAwesomeIcon icon={faPrint} color="#3c8ac9" className="_text-2xl _mx-2"
                        id="CancelarOrcamento_IconeImprimirComprovante"
                    />
                    Comprovante de Cancelamento
                </button>
            )}
            {props.permitidoImprimirReciboBaixaCancelamento  && (
                <button className="_my-6 no-global-button"
                    id="CancelarOrcamento_BotaoImprimirReciboBaixaCancelamento"
                    key="CancelarOrcamento_BotaoImprimirReciboBaixaCancelamento"
                    onClick={() => props.openModalImpressao('ReciboBaixaCancelamento')}>
                    <FontAwesomeIcon 
                        icon={faPrint} 
                        color="#3c8ac9" 
                        className="_text-2xl _mx-2"
                        id="CancelarOrcamento_IconeImprimirReciboBaixaCancelamento"
                        key="CancelarOrcamento_IconeImprimirReciboBaixaCancelamento" 
                    />
                    Recibo de Baixa de Contas a Receber
                </button>
            )}
            <section style={{ marginBottom: "100px" }} />
        </>
    );
});